import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Activity, Sun, AlertTriangle, Zap, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useApp } from '@/contexts/AppContext';
import { useFleetAnalytics } from '@/hooks/useFleetAnalytics';
import { PeriodSelector, periodToDates, type AnalyticsPeriod } from './PeriodSelector';
import { AnalyticsSummaryCard } from './AnalyticsSummaryCard';
import { AnalyticsSkeleton } from './AnalyticsSkeleton';
import { EnergyTrendChart } from './EnergyTrendChart';
import { PropertyPerformanceTable } from './PropertyPerformanceTable';
import { ActionItemsSection, NoActionItems } from './ActionItemsSection';
import { EnergyBreakdownSection } from './EnergyBreakdownSection';
import { CollapsibleSection } from './CollapsibleSection';
import { handleAnchorScroll } from '@/lib/navigation';

export function InstallerAnalyticsView() {
  const { organization } = useApp();
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const { dateFrom, dateTo } = periodToDates(period);

  const location = useLocation();
  const { data, isLoading, error } = useFleetAnalytics(organization?.id, dateFrom, dateTo);

  // Handle anchor scrolling from cross-page navigation (e.g., /app/analytics#energy-breakdown)
  useEffect(() => {
    if (data && location.hash) {
      handleAnchorScroll(location.hash, 300);
    }
  }, [data, location.hash]);

  // Compute whether there are any action items (for deciding whether to show NoActionItems)
  const hasActionItems = useMemo(() => {
    if (!data?.properties) return false;
    return data.properties.some(
      (p) => p.flag_battery_expansion || p.flag_chronic_curtailment || p.flag_battery_replacement
    );
  }, [data?.properties]);

  // Collapsed summary for energy trend
  const trendSummary = useMemo(() => {
    if (!data) return '';
    const curtPct = data.fleet_pv_kwh > 0
      ? ((data.fleet_curtailed_kwh / data.fleet_pv_kwh) * 100).toFixed(1)
      : '0';
    return `${data.fleet_pv_kwh.toFixed(0)} kWh generated · ${data.fleet_curtailed_kwh.toFixed(0)} kWh curtailed (${curtPct}%) · ${data.period_days} day period`;
  }, [data]);

  // Collapsed summary for property table — top 3 underperformers by self-consumption
  const tableSummary = useMemo(() => {
    if (!data?.properties || data.properties.length === 0) return '';
    const sorted = [...data.properties]
      .filter((p) => p.pv_kwh > 0) // only properties with solar data
      .sort((a, b) => a.self_consumption_pct - b.self_consumption_pct)
      .slice(0, 3);
    if (sorted.length === 0) return 'No data for this period';
    const names = sorted.map((p) => `${p.name} (${p.self_consumption_pct.toFixed(1)}%)`);
    return `Lowest self-consumption: ${names.join(', ')}`;
  }, [data?.properties]);

  if (!organization) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-12 flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-wt-text-secondary mb-4" />
          <p className="text-wt-text-secondary">No organization found</p>
        </CardContent>
      </Card>
    );
  }

  const rateDollars = data ? `$${(data.fleet_avg_rate_cents / 100).toFixed(2)}` : '$0.12';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Fleet Analytics</h1>
          <p className="text-wt-text-secondary mt-1">
            Performance across {data?.property_count ?? '...'} properties
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {isLoading && <AnalyticsSkeleton />}

      {error && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-8 w-8 text-destructive mb-4" />
            <p className="text-sm text-foreground">Failed to load fleet analytics</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* KPI Cards — always visible */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <AnalyticsSummaryCard
              label="Fleet Uptime"
              value={`${data.fleet_uptime_pct.toFixed(1)}%`}
              subtitle={`${data.properties?.filter((p) => p.uptime_pct > 0).length ?? 0} of ${data.property_count} reporting`}
              icon={<Activity className="w-5 h-5" />}
              color={data.fleet_uptime_pct >= 95 ? 'green' : data.fleet_uptime_pct >= 80 ? 'amber' : 'red'}
            />
            <AnalyticsSummaryCard
              label="Total Generated"
              value={`${data.fleet_pv_kwh.toFixed(0)} kWh`}
              subtitle={`~$${data.fleet_savings_dollars.toFixed(0)} estimated value*`}
              icon={<Sun className="w-5 h-5" />}
              color="default"
            />
            <AnalyticsSummaryCard
              label="Issues"
              value={`${data.sent_count} detected`}
              subtitle={`${data.resolved_count} resolved`}
              icon={<AlertTriangle className="w-5 h-5" />}
              color={data.sent_count > data.resolved_count ? 'amber' : data.sent_count > 0 ? 'green' : 'default'}
            />
            <AnalyticsSummaryCard
              label="Curtailed"
              value={`${data.fleet_curtailed_kwh.toFixed(1)} kWh`}
              subtitle={data.fleet_curtailed_kwh > 0 ? `~$${(data.fleet_curtailed_kwh * data.fleet_avg_rate_cents / 100).toFixed(0)} wasted*` : undefined}
              icon={<Zap className="w-5 h-5" />}
              color={data.fleet_curtailed_kwh > 50 ? 'amber' : 'default'}
            />
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">
            * based on fleet avg {rateDollars}/kWh
          </p>

          {/* Action Items — expanded by default, or green empty state */}
          {hasActionItems && data.properties ? (
            <ActionItemsSection
              properties={data.properties}
              periodDays={data.period_days}
            />
          ) : (
            <NoActionItems />
          )}

          {/* Energy Breakdown — expanded by default */}
          <CollapsibleSection
            id="energy-breakdown"
            title="Energy Breakdown"
            collapsedSummary={`${data.fleet_pv_kwh.toFixed(0)} kWh PV · ${data.fleet_grid_import_kwh.toFixed(0)} kWh grid · ${data.fleet_curtailed_kwh.toFixed(0)} kWh curtailed`}
            defaultExpanded={true}
          >
            <EnergyBreakdownSection data={data} />
          </CollapsibleSection>

          {/* Fleet Energy Trend — collapsed by default */}
          {data.daily_trend && data.daily_trend.length > 0 && (
            <CollapsibleSection
              title="Fleet Energy Trend"
              collapsedSummary={trendSummary}
            >
              <EnergyTrendChart data={data.daily_trend} title="Fleet Energy Trend" />
            </CollapsibleSection>
          )}

          {/* Property Performance — collapsed by default */}
          {data.properties && data.properties.length > 0 && (
            <CollapsibleSection
              title="Property Performance"
              badge={data.property_count}
              collapsedSummary={tableSummary}
            >
              <PropertyPerformanceTable
                properties={data.properties}
                fleetAvgRateCents={data.fleet_avg_rate_cents}
              />
            </CollapsibleSection>
          )}
        </>
      )}
    </div>
  );
}
